import React from 'react';
import {
  Droplets,
  Shield,
  CheckCircle2,
  HeartPulse,
  ChevronRight,
  Info,
  Brush,
  AlertCircle,
  MapPin
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';

const PielPelajeCard = ({ pielYPelaje }) => {
  if (!pielYPelaje) return null;

  return (
    <Card className="overflow-hidden bg-white">
      {/* Encabezado del Card */}
      <CardHeader className="border-b bg-gradient-to-r from-purple-500/10 to-transparent p-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg shadow-sm">
            <Droplets className="h-5 w-5 text-white" />
          </div>
          <div>
            <CardTitle className="text-xl font-semibold text-gray-800">
              Piel y Pelaje
            </CardTitle>
            <p className="text-base text-gray-500 mt-1">
              Evaluación del estado y cuidados necesarios
            </p>
          </div>
        </div>
      </CardHeader>

      {/* Contenido principal */}
      <CardContent className="p-4 space-y-4">
        {/* Estado General */}
        <div className="bg-white rounded-lg border border-gray-100 shadow-sm">
          <div className="p-4 space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Shield className="h-5 w-5 text-purple-500" />
                  <h3 className="text-lg font-medium text-gray-900">
                    Estado General
                  </h3>
                </div>
                <p className="text-base text-gray-700">{pielYPelaje.estado}</p>
              </div>

              {pielYPelaje.caracteristicas?.condicion && (
                <span
                  className={cn(
                    'inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium',
                    'bg-purple-100 text-purple-700'
                  )}
                >
                  {pielYPelaje.caracteristicas.condicion}
                </span>
              )}
            </div>

            {/* Problemas y Alertas */}
            {pielYPelaje.problemas && pielYPelaje.problemas.length > 0 && (
              <div className="bg-orange-50/50 rounded-lg p-4 border border-orange-100">
                <div className="flex items-center gap-2 mb-3">
                  <AlertCircle className="h-5 w-5 text-orange-500" />
                  <h4 className="text-lg font-medium text-gray-900">
                    Aspectos a Vigilar
                  </h4>
                </div>
                <div className="space-y-3">
                  {pielYPelaje.problemas.map((problema, idx) => (
                    <div
                      key={idx}
                      className="flex flex-col gap-3 bg-white/60 p-3 rounded-lg hover:bg-white/80 transition-colors"
                    >
                      {/* Qué ocurre */}
                      <div className="flex items-start gap-2">
                        <Info className="h-5 w-5 mt-0.5 text-orange-500" />
                        <div>
                          <p className="text-base font-medium text-gray-700">
                            Qué ocurre
                          </p>
                          <p className="text-base text-gray-600">
                            {problema.que}
                          </p>
                        </div>
                      </div>
                      {/* Dónde */}
                      <div className="flex items-start gap-2">
                        <MapPin className="h-5 w-5 mt-0.5 text-orange-500" />
                        <div>
                          <p className="text-base font-medium text-gray-700">
                            Ubicación
                          </p>
                          <p className="text-base text-gray-600">
                            {problema.donde}
                          </p>
                        </div>
                      </div>
                      {/* Qué hacer */}
                      <div className="flex items-start gap-2">
                        <ChevronRight className="h-5 w-5 mt-0.5 text-orange-500" />
                        <div>
                          <p className="text-base font-medium text-gray-700">
                            Acción requerida
                          </p>
                          <p className="text-base text-gray-600">
                            {problema.queHacer}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Características y Cuidados */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Características del Pelaje */}
          {pielYPelaje.caracteristicas && (
            <div className="bg-gradient-to-br from-purple-50 to-white rounded-lg border border-purple-100 hover:border-purple-200 transition-colors">
              <div className="p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <Brush className="h-5 w-5 text-purple-500" />
                  <h4 className="text-lg font-medium text-gray-900">
                    Características del Pelaje
                  </h4>
                </div>
                <div className="space-y-3 mt-3">
                  {Object.entries(pielYPelaje.caracteristicas).map(
                    ([key, value], idx) =>
                      value &&
                      key !== 'condicion' && (
                        <div
                          key={idx}
                          className="flex items-start gap-2 bg-white/60 p-3 rounded-lg"
                        >
                          <CheckCircle2 className="h-5 w-5 mt-0.5 text-purple-500" />
                          <div>
                            <p className="text-base font-medium text-gray-700 capitalize">
                              {key}
                            </p>
                            <p className="text-base text-gray-600">{value}</p>
                          </div>
                        </div>
                      )
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Cuidados Recomendados */}
          {pielYPelaje.cuidadosRecomendados && (
            <div className="bg-gradient-to-br from-purple-50 to-white rounded-lg border border-purple-100">
              <div className="p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <HeartPulse className="h-5 w-5 text-purple-500" />
                  <h4 className="text-lg font-medium text-gray-900">
                    Plan de Cuidados
                  </h4>
                </div>
                <div className="space-y-2 mt-3">
                  {Object.entries(pielYPelaje.cuidadosRecomendados).map(
                    ([key, value], idx) =>
                      value && (
                        <div
                          key={idx}
                          className="group flex items-start gap-2 bg-white/60 p-3 rounded-lg hover:bg-white/80 transition-colors border border-transparent hover:border-purple-100 cursor-pointer"
                        >
                          <ChevronRight className="h-5 w-5 mt-0.5 text-purple-500 group-hover:translate-x-0.5 transition-transform" />
                          <div>
                            <p className="text-base font-medium text-purple-700 capitalize">
                              {key}
                            </p>
                            <p className="text-base text-gray-600">{value}</p>
                          </div>
                        </div>
                      )
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Alertas Finales: Cuándo preocuparse */}
        {pielYPelaje.cuidadosRecomendados?.cuandoPreocuparse && (
          <div className="bg-orange-50/50 rounded-lg border border-orange-100 p-4">
            <div className="flex items-center gap-2 mb-3">
              <AlertCircle className="h-5 w-5 text-orange-500" />
              <h4 className="text-lg font-medium text-gray-900">
                Cuándo Contactar al Veterinario
              </h4>
            </div>
            <p className="text-base text-orange-700 bg-white/60 p-3 rounded-lg">
              {pielYPelaje.cuidadosRecomendados.cuandoPreocuparse}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PielPelajeCard;
